import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { CloseIcon } from '@components/Icons';
import {
  useAudioVideo,
  useSessionFeatureState,
} from '@hooks/storeHooks/useSessionState';
import { useLocation } from 'react-router-dom';
import { useContentBounds } from '../../hooks/useContentBounds';
import { useDrag } from '../../hooks/useDrag';
import VideoContainer from '../VideoContainer';

const CONTAINER_WIDTH = 160;
const WORKFLOW_AVATAR_PROXY_ID = 'workflow-avatar-proxy';

const SessionContainer: React.FC = () => {
  const location = useLocation();
  const {
    isSessionPage,
    taskId,
    isDesignerSession,
    avatarReady,
    companionSlug,
    resetSessionState,
  } = useSessionFeatureState();
  const isGenerationSession = !!taskId || isDesignerSession;
  const { webRTCStatus } = useAudioVideo();
  const isRateLimitedHidden =
    webRTCStatus?.errorStatus === 429 && !!webRTCStatus?.blocked;

  const isContributorPage = location.pathname.startsWith('/contributor/');

  const bounds = useContentBounds();

  const [workflowAvatarHost, setWorkflowAvatarHost] =
    useState<HTMLElement | null>(null);
  const [isIdleVideoLoaded, setIsIdleVideoLoaded] = useState(false);

  // react flow creates this a frame late — don't read it in render or we never portal
  // and the whole workflow stays broken (avatarReady never goes true)
  useLayoutEffect(() => {
    if (!isGenerationSession) {
      setWorkflowAvatarHost(null);
      return;
    }

    let cancelled = false;
    let rafId = 0;
    let attempts = 0;
    const maxAttempts = 90;

    const tryAttach = () => {
      if (cancelled) return true;
      const el = document.getElementById(WORKFLOW_AVATAR_PROXY_ID);
      if (el) {
        setWorkflowAvatarHost((prev) => (prev === el ? prev : el));
        return true;
      }
      return false;
    };

    if (tryAttach()) {
      return () => {
        cancelled = true;
      };
    }

    const tick = () => {
      if (cancelled) return;
      if (tryAttach()) return;
      attempts += 1;
      if (attempts >= maxAttempts) return;
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [isGenerationSession, taskId, isDesignerSession]);

  const getCenterY = () => {
    return isSessionPage
      ? window.innerHeight * 0.15
      : isContributorPage
        ? window.innerHeight * 0.11
        : window.innerHeight * 0.05;
  };

  const { position, isDragging, bind, hasBeenMoved, setPosition } = useDrag({
    disabled: !isIdleVideoLoaded,
    constrainToViewport: true,
    initialPosition: {
      x: bounds.centerX,
      y: getCenterY(),
    },
    boundsLeft: bounds.left,
    boundsRight: bounds.right,
  });

  // Re-center when bounds change (resize, sidebar/queue toggle) if user hasn't dragged
  useEffect(() => {
    if (!hasBeenMoved) {
      setPosition({ x: bounds.centerX, y: getCenterY() });
    }
  }, [bounds.centerX, isSessionPage, hasBeenMoved, setPosition]);

  // Re-constrain position if user has dragged and bounds shrink (e.g. queue opens)
  useEffect(() => {
    if (!hasBeenMoved) return;
    const maxX = bounds.right - CONTAINER_WIDTH;
    const clampedX = Math.max(bounds.left, Math.min(position.x, maxX));
    if (clampedX !== position.x) {
      setPosition({ x: clampedX, y: position.y });
    }
  }, [bounds.left, bounds.right]);

  useEffect(() => {
    return () => {
      resetSessionState(true);
    };
  }, [location.pathname]);

  useEffect(() => {
    if (
      !isContributorPage &&
      !isSessionPage &&
      !avatarReady &&
      !companionSlug
    ) {
      resetSessionState(true);
    }
  }, [
    location.pathname,
    isContributorPage,
    isSessionPage,
    avatarReady,
    companionSlug,
    resetSessionState,
  ]);

  if (isGenerationSession) {
    if (!workflowAvatarHost) {
      return null;
    }
    return createPortal(
      <div
        className="text-white w-40 pointer-events-auto"
        style={isRateLimitedHidden ? { display: 'none' } : undefined}>
        <VideoContainer
          isIdleVideoLoaded={isIdleVideoLoaded}
          setIsIdleVideoLoaded={setIsIdleVideoLoaded}
        />
      </div>,
      workflowAvatarHost,
    );
  }

  return (
    <div
      {...bind}
      className={`fixed z-[99999] text-white w-40 ${
        !isIdleVideoLoaded
          ? 'cursor-default'
          : isDragging
            ? 'cursor-grabbing'
            : 'cursor-grab'
      }`}
      style={{
        transform: `translate(${position.x}px, ${position.y}px)`,
        ...(isRateLimitedHidden ? { display: 'none' } : null),
      }}>
      {!isSessionPage && !isContributorPage && (
        <button
          className="absolute -top-2 -right-2 text-white/80 hover:text-white z-10 cursor-pointer"
          onClick={() => resetSessionState(true)}>
          <CloseIcon width={16} height={16} />
        </button>
      )}
      <VideoContainer
        isIdleVideoLoaded={isIdleVideoLoaded}
        setIsIdleVideoLoaded={setIsIdleVideoLoaded}
      />
    </div>
  );
};

export default SessionContainer;
